// modules/Multiplayer/MultiplayerConnection.js
// 多人游戏连接管理模块

import NetworkManager from '../NetworkManager.js';
import DebugHelper from '../DebugHelper.js';
import ServerConfig from '../ServerConfig.js';
import GameStateStore from '../GameStateStore.js';

export default class MultiplayerConnection {
    constructor(scene, ui, roomList) {
        this.scene = scene;
        this.ui = ui;
        this.roomList = roomList;
        
        this.connectionStatus = 'disconnected'; // disconnected, connecting, connected, error
        this.serverUrl = ServerConfig.getServerUrl(); // 根据环境自动选择服务器地址
        this.playerName = GameStateStore.getPlayer()?.name || window.userInfo?.username || '玩家';
        this.userId = window.userInfo?.userId || null;
        this.networkManager = null;
        this.isConnectedToServer = false;
        this.roomId = '';
        this.isTransitioning = false; // 标记是否正在切换场景
    }
    
    /**
     * 初始化连接管理器
     * @param {Object} config - 配置对象 {serverUrl, playerName}
     */
    init(config = {}) {
        this.serverUrl = config.serverUrl || this.serverUrl;
        // 确保 playerName 不为空
        if (config.playerName && config.playerName.trim() !== '') {
            this.playerName = config.playerName.trim();
        } else if (!this.playerName || this.playerName.trim() === '') {
            this.playerName = GameStateStore.getPlayer()?.name || window.userInfo?.username || '玩家';
        }
    }
    
    /**
     * 连接到服务器
     * @param {Function} onConnect - 连接成功回调
     * @param {Function} onError - 连接失败回调
     * @param {Function} onRoomList - 房间列表更新回调
     * @param {Function} onRoomCreated - 房间创建回调
     * @param {Function} onJoined - 加入房间成功回调
     */
    connectToServer(onConnect = null, onError = null, onRoomList = null, onRoomCreated = null, onJoined = null) {
        if (this.connectionStatus === 'connecting' || this.isConnectedToServer) {
            return;
        }
        
        // 验证输入
        const playerName = this.ui.getInputFieldValue('playerName');
        const serverUrl = this.ui.getInputFieldValue('serverUrl');
        
        // 验证玩家名称和服务器地址不为空
        if (!playerName || playerName.trim() === '' || !serverUrl || serverUrl.trim() === '') {
            this.ui.updateStatus('请填写玩家名称和服务器地址', '#ff0000');
            return;
        }
        
        // 使用去除空格的玩家名称
        this.playerName = playerName.trim();
        this.serverUrl = serverUrl.trim();
        
        // 更新状态
        this.connectionStatus = 'connecting';
        this.ui.updateStatus('正在连接服务器...', '#ffaa00');
        this.ui.updateConnectButton('连接中...', '#888888', false);
        
        // 创建新的网络管理器（仅连接，不加入房间）
        this.networkManager = new NetworkManager(this.scene);
        
        // 设置事件处理器
        this.networkManager.on('connect', () => {
            this.connectionStatus = 'connected';
            this.isConnectedToServer = true;
            // 只有在场景未切换时才更新 UI
            if (!this.isTransitioning && this.scene && this.scene.scene && this.scene.scene.isActive()) {
                this.ui.updateStatus('连接成功！', '#00ff00');
                this.ui.updateConnectButton('已连接', '#00ff00', false);
            }
            
            // 注册房间列表事件
            this.networkManager.on('roomList', (rooms) => {
                if (onRoomList) {
                    onRoomList(rooms);
                } else {
                    this.roomList.updateRoomList(rooms);
                }
            });
            
            this.networkManager.on('roomCreated', (roomId, roomInfo) => {
                // 只有在场景未切换时才更新 UI
                if (!this.isTransitioning && this.scene && this.scene.scene && this.scene.scene.isActive()) {
                    this.ui.updateStatus(`房间创建成功: ${roomInfo.name}`, '#00ff00');
                }
                if (onRoomCreated) {
                    onRoomCreated(roomId, roomInfo);
                }
            });
            
            this.networkManager.on('joined', (payload) => {
                // 只有在场景未切换时才更新 UI
                if (!this.isTransitioning && this.scene && this.scene.scene && this.scene.scene.isActive()) {
                    this.ui.updateStatus('加入房间成功！正在进入游戏...', '#00ff00');
                }
                if (onJoined) {
                    onJoined(payload);
                }
            });
            
            if (onConnect) {
                onConnect();
            }
        });
        
        this.networkManager.on('error', (error) => {
            this.connectionStatus = 'error';
            this.isConnectedToServer = false;
            // 只有在场景未切换时才更新 UI
            if (!this.isTransitioning && this.scene && this.scene.scene && this.scene.scene.isActive()) {
                this.ui.updateStatus('连接失败: ' + (error.message || '未知错误'), '#ff0000');
                this.ui.updateConnectButton('连接服务器', '#ffffff', true);
            }
            
            if (onError) {
                onError(error);
            }
        });
        
        // 连接到服务器（不指定房间ID，仅建立连接）
        // 注意：连接服务器和加入房间是两个独立的操作
        // 这里只建立 WebSocket 连接，不发送 join 消息
        // 只有在用户明确操作（创建/加入房间）时才会发送 join 消息
        this.networkManager.connect(
            this.serverUrl,
            '', // 不指定房间ID
            '', // 不传入 playerName，避免自动加入房间
            {}  // 不传入 playerData
        );
    }
    
    /**
     * 刷新房间列表
     */
    refreshRoomList() {
        if (this.networkManager && this.isConnectedToServer) {
            this.networkManager.getRoomList();
            this.ui.updateStatus('正在刷新房间列表...', '#ffaa00');
        }
    }
    
    /**
     * 创建房间
     * @param {string} roomName - 房间名称
     * @param {number} maxPlayers - 最大玩家数
     */
    createRoom(roomName, maxPlayers = 10) {
        if (this.networkManager && this.isConnectedToServer) {
            this.networkManager.createRoom(roomName, maxPlayers);
            this.ui.updateStatus('正在创建房间...', '#ffaa00');
        }
    }
    
    /**
     * 加入房间
     * @param {string} roomId - 房间ID
     */
    joinRoom(roomId) {
        if (!this.networkManager || !this.isConnectedToServer) {
            this.ui.updateStatus('请先连接服务器', '#ff0000');
            return;
        }
        
        this.roomId = roomId;
        this.ui.updateStatus('正在加入房间...', '#ffaa00');
        
        // 发送加入房间消息
        // 检查gameState.player是否有效，如果无效则使用空对象（避免发送全0数据）
        let playerData = {};
        const gameStatePlayer = GameStateStore.getPlayer();
        if (gameStatePlayer) {
            // 检查是否有有效数据（至少有一个非0的属性）
            const hasValidData = gameStatePlayer.hp > 0 || gameStatePlayer.maxHp > 0 || 
                                gameStatePlayer.attack > 0 || gameStatePlayer.defense > 0 ||
                                gameStatePlayer.money > 0 || gameStatePlayer.exp > 0 ||
                                (gameStatePlayer.level && gameStatePlayer.level > 0);
            
            if (hasValidData) {
                // 只发送有效的属性，避免发送0值覆盖服务器上的数据
                playerData = {
                    name: gameStatePlayer.name,
                    element: gameStatePlayer.element,
                    battle: gameStatePlayer.battle,
                    hp: gameStatePlayer.hp > 0 ? gameStatePlayer.hp : undefined,
                    maxHp: gameStatePlayer.maxHp > 0 ? gameStatePlayer.maxHp : undefined,
                    attack: gameStatePlayer.attack > 0 ? gameStatePlayer.attack : undefined,
                    defense: gameStatePlayer.defense > 0 ? gameStatePlayer.defense : undefined,
                    money: gameStatePlayer.money > 0 ? gameStatePlayer.money : undefined,
                    exp: gameStatePlayer.exp > 0 ? gameStatePlayer.exp : undefined,
                    level: gameStatePlayer.level > 0 ? gameStatePlayer.level : undefined,
                    nextLevelExp: gameStatePlayer.nextLevelExp > 0 ? gameStatePlayer.nextLevelExp : undefined,
                    inventory: gameStatePlayer.inventory || [],
                    equipment: gameStatePlayer.equipment || [],
                    skills: gameStatePlayer.skills || [],
                    vehicle: gameStatePlayer.vehicle || undefined
                };
                // 移除undefined属性
                Object.keys(playerData).forEach(key => {
                    if (playerData[key] === undefined) {
                        delete playerData[key];
                    }
                });
            } else {
                // 数据无效，只发送基本信息
                playerData = {
                    name: gameStatePlayer.name || this.playerName,
                    element: gameStatePlayer.element || 'player1'
                };
            }
        } else {
            // gameState不存在，只发送基本信息
            playerData = {
                name: this.playerName,
                element: 'player1'
            };
        }
        
        // 如果用户已登录，传递userId
        const joinPayload = {
            roomId: roomId,
            playerName: this.playerName,
            playerData: playerData
        };
        
        if (this.userId) {
            joinPayload.userId = this.userId;
        }
        
        this.networkManager.send('join', joinPayload);
    }
    
    /**
     * 断开连接
     */
    disconnect() {
        if (this.networkManager) {
            this.networkManager.disconnect();
            this.networkManager = null;
        }
        this.connectionStatus = 'disconnected';
        this.isConnectedToServer = false;
        this.roomId = '';
    }
    
    /**
     * 获取网络管理器
     * @returns {NetworkManager|null} 网络管理器
     */
    getNetworkManager() {
        return this.networkManager;
    }
    
    /**
     * 获取连接状态
     * @returns {string} 连接状态
     */
    getConnectionStatus() {
        return this.connectionStatus;
    }
    
    /**
     * 是否已连接到服务器
     * @returns {boolean} 是否已连接
     */
    isConnected() {
        return this.isConnectedToServer;
    }
}

